<script lang="ts" setup>
import { InputAutoCompletion } from '@sancare/typescript-commons'
import { Dayjs } from 'dayjs'
import _ from 'lodash'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { Diagnosis, DiagnosisType } from '@/store/modules/diagnosis/types'


const store = useStore()

const props = defineProps<{
  diagnosis: Diagnosis | null
  diagnosisType: DiagnosisType
  rhsId: number
  rhsEndDate: Dayjs
  currentDiagnosisTypeFormDisplayed: DiagnosisType | null
}>()

const emit = defineEmits<{
  (e: 'display-form', isFormDisplayed: boolean): void
}>()

const isFormDisplayed = ref(false)
const typedDiagnosisReference = ref('')
const isDiagnosisCompletionDisplayed = ref(false)
const selectedDiagnosisReference = ref('')
const addDiagnosisRequest = ref({ fetching: false, error: null, ok: true })
let abortController: AbortController

const diagnosisSearchResults = computed(
  () => store.state.stayDisplayer.diagnosisSearchResults[props.diagnosisType]
)
const diagnosisSearchRequest = computed(
  () => store.state.stayDisplayer.searchDiagnosesRequest[props.diagnosisType]
)

const isTypedDiagnosisEmpty = computed(() => typedDiagnosisReference.value === '')

const resetAddDiagnosisRequest = () => {
  addDiagnosisRequest.value = { fetching: false, error: null, ok: false }
}

const displayForm = (isDisplayed: boolean) => {
  emit('display-form', isDisplayed)
  isFormDisplayed.value = isDisplayed
  typedDiagnosisReference.value = ''
  resetAddDiagnosisRequest()
}

const displayCompletion = (isDisplayed: boolean) => {
  isDiagnosisCompletionDisplayed.value = isDisplayed
}

const selectItem = (diag: Diagnosis) => {
  selectedDiagnosisReference.value = diag.reference
  typedDiagnosisReference.value = diag.reference
}

const addDiagnosis = async () => {
  resetAddDiagnosisRequest()
  addDiagnosisRequest.value.fetching = true

  const response: { error: string } | null = await store.dispatch('smrDiagnosis/addSmrDiagnosis', {
    body: {
      newDiagnosisType: props.diagnosisType,
      diagnosis: typedDiagnosisReference.value,
    },
    urlParams: {
      rhsId: props.rhsId,
    },
  })

  if (response?.error) {
    addDiagnosisRequest.value.error = response.error
  } else {
    addDiagnosisRequest.value.ok = true
    displayForm(false)
  }

  addDiagnosisRequest.value.fetching = false
}

watch(typedDiagnosisReference, _.debounce(() => {
  if (abortController) {
    abortController.abort()
  }
  if (selectedDiagnosisReference.value && selectedDiagnosisReference.value === typedDiagnosisReference.value) {
    return
  }

  if (typedDiagnosisReference.value) {
    abortController = new AbortController()
    const signal = abortController.signal

    store.dispatch('searchDiagnoses', {
      diagnosisType: props.diagnosisType,
      search: typedDiagnosisReference.value,
      contextDate: props.rhsEndDate.format('YYYY-MM-DD'),
      signal,
      searchByType: true,
    })
  } else {
    store.commit('clearSearchDiagnoses', props.diagnosisType)
  }
}, 500))

watch(() => props.currentDiagnosisTypeFormDisplayed, (newFormDisplayed) => {
  if(newFormDisplayed !== props.diagnosisType) {
    isFormDisplayed.value = false
  }
})

watch(() => props.rhsId, () => {
  displayForm(false)
})
</script>

<template>
  <div class="aligned-row mt-2">
    <div v-if="!isFormDisplayed">
      <button
        type="button"
        class="col-12 btn btn-sm"
        @click="displayForm(true)"
      >
        {{ !diagnosis || diagnosisType === DiagnosisType.DAS
          ? `Ajouter un ${diagnosisType}`
          : `Modifier le ${diagnosisType}` }}
      </button>
    </div>

    <div v-else>
      <input
        v-model="typedDiagnosisReference"
        v-on-click-outside="() => displayCompletion(false)"
        v-focus
        type="text"
        class="form-control mt-3"
        :class="{'mb-2': !addDiagnosisRequest.error}"
        @keydown="() => displayCompletion(true)"
        @keydown.esc="() => displayCompletion(false)"
      >
      <input-auto-completion
        v-show="isDiagnosisCompletionDisplayed && !isTypedDiagnosisEmpty"
        :completion-list="diagnosisSearchResults"
        :is-loading="diagnosisSearchRequest.fetching"
        :title-prop="'reference'"
        :description-prop="'description'"
        @select-item="selectItem"
      />
      <div
        v-if="addDiagnosisRequest.error"
        class="error-block mt-1 mb-2"
      >
        {{ addDiagnosisRequest.error }}
      </div>
      <div class="d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-sm btn-outline me-2 button-diagnosis"
          @click="displayForm(false)"
        >
          Annuler
        </button>
        <button
          type="button"
          class="btn btn-sm button-diagnosis"
          :class="{'disabled': isTypedDiagnosisEmpty}"
          :disabled="isTypedDiagnosisEmpty"
          @click="addDiagnosis"
        >
          <span v-if="!addDiagnosisRequest.fetching">Valider</span>
          <div
            v-else
            class="loader loader-xs loader-purple"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.button-diagnosis {
  width: 72px;
}
</style>
